import {
  Box, Card, Chip, Dialog, Icon, IconButton, TextField, Tooltip, Typography, useTheme
} from '@mui/material';
import { useCallback, useState } from 'react';
import { dateFormat } from '../../../helpers/dateFormat';
import { IAppointmentView } from '../../../services';
import { AppointmentFinancePage } from './components/AppointmentFinancePage';

interface IProps {
  appointments: IAppointmentView[];
  refetch: () => void;
  completed: boolean;
}

export const AppointmentFinance: React.FC<IProps> = ({ appointments, refetch, completed }) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [id, setId] = useState<number>();
  const [search, setSearch] = useState('');
  const theme = useTheme();

  const onClickEdit = useCallback((id: number) => {
    setOpenDialog(true);
    setId(id);
  }, []);

  const handleClose = useCallback(() => {
    setOpenDialog(false);
    setId(undefined);
  }, []);

  const filtered = appointments.filter(a =>
    !search ||
    a.customerName?.toLowerCase().includes(search.toLowerCase()) ||
    a.employeeName?.toLowerCase().includes(search.toLowerCase())
  );

  if (!appointments) return null;

  return (
    <Box display='flex' flexDirection='column' gap={1.5}>

      {/* Search */}
      <TextField
        size='small'
        placeholder='Buscar cliente ou profissional...'
        value={search}
        onChange={e => setSearch(e.target.value)}
        InputProps={{
          startAdornment: <Icon sx={{ mr: 1, opacity: 0.5 }}>search</Icon>
        }}
      />

      {filtered.length === 0 && (
        <Box textAlign='center' py={4}>
          <Icon sx={{ fontSize: 48, opacity: 0.2 }}>event_available</Icon>
          <Typography color='text.secondary' mt={1}>
            {search ? 'Nenhum resultado encontrado.' : 'Nenhum agendamento aqui.'}
          </Typography>
        </Box>
      )}

      {filtered.map(appointment => (
        <Card
          key={appointment.id}
          elevation={0}
          sx={{
            display: 'flex',
            alignItems: 'stretch',
            borderRadius: '12px',
            border: '1px solid',
            borderColor: 'divider',
            overflow: 'hidden',
            transition: 'box-shadow 0.2s',
            '&:hover': { boxShadow: '0 4px 16px rgba(0,0,0,0.12)' }
          }}
        >
          {/* Employee color bar */}
          <Box sx={{ width: 6, backgroundColor: appointment.employeeColor || theme.palette.primary.main, flexShrink: 0 }} />

          {/* Left: customer info + services */}
          <Box flex={1} px={2} py={1.5} display='flex' flexDirection='column' gap={0.5} sx={{ minWidth: 0 }}>
            <Box display='flex' alignItems='center' gap={1}>
              <Typography variant='subtitle2' fontWeight={700} noWrap>
                {appointment.customerName || 'Cliente'}
              </Typography>
              {appointment.appointmentNumber > 0 && (
                <Chip
                  label={`#${appointment.appointmentNumber}`}
                  size='small'
                  variant='outlined'
                  color='primary'
                  sx={{ height: 20, fontSize: '0.7rem', flexShrink: 0 }}
                />
              )}
            </Box>
            <Typography variant='caption' color='text.secondary'>
              {appointment.employeeName} · {dateFormat(appointment.date, 'LL')} às {dateFormat(appointment.date, 'LT')}
            </Typography>
            <Box display='flex' flexWrap='wrap' gap={0.5}>
              {appointment.services?.map(s => (
                <Chip key={s.id} label={s.description} size='small' variant='outlined' />
              ))}
              {appointment.products?.map(p => (
                <Chip key={p.id} label={p.description} size='small' variant='outlined' color='secondary' />
              ))}
            </Box>
          </Box>

          {/* Center: R$ amount */}
          <Box display='flex' alignItems='center' px={2}>
            <Typography variant='subtitle1' fontWeight={700} color='success.main' sx={{ whiteSpace: 'nowrap' }}>
              R$ {appointment.totalAmount?.toFixed(2)}
            </Typography>
          </Box>

          {/* Right: Action button */}
          <Box display='flex' alignItems='center' pr={1}>
            <Tooltip title={completed ? 'Ver detalhes' : 'Finalizar agendamento'}>
              <IconButton
                onClick={() => onClickEdit(appointment.id)}
                sx={{
                  backgroundColor: completed ? 'transparent' : theme.palette.primary.main,
                  color: completed ? 'text.secondary' : '#fff',
                  borderRadius: '10px',
                  '&:hover': { backgroundColor: completed ? 'action.hover' : theme.palette.primary.dark }
                }}
              >
                <Icon>{completed ? 'visibility' : 'check_circle'}</Icon>
              </IconButton>
            </Tooltip>
          </Box>
        </Card>
      ))}

      {id && (
        <Dialog open={openDialog} onClose={handleClose} maxWidth='sm' fullWidth>
          <AppointmentFinancePage
            appointmentId={id}
            refetch={refetch}
            onCloseEdit={handleClose}
          />
        </Dialog>
      )}
    </Box>
  );
};
